import { useRef, useState, type ChangeEvent } from "react";
import Modal from "@/components/base/Modal";
import { ghostButtonClass } from "@/components/base/fieldStyles";
import type { ExtractedImage } from "@/utils/pdfCatalog";

interface ImageAssignModalProps {
  open: boolean;
  images: ExtractedImage[];
  currentUrl: string;
  onPick: (dataUrl: string) => void;
  onClose: () => void;
}

const fileToThumb = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        try {
          const max = 320;
          const scale = Math.min(1, max / Math.max(img.width, img.height));
          const canvas = document.createElement("canvas");
          canvas.width = Math.max(1, Math.round(img.width * scale));
          canvas.height = Math.max(1, Math.round(img.height * scale));
          const ctx = canvas.getContext("2d");
          if (!ctx) return resolve(String(reader.result));
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/jpeg", 0.72));
        } catch {
          resolve(String(reader.result));
        }
      };
      img.onerror = () => reject(new Error("No se pudo leer la imagen."));
      img.src = String(reader.result);
    };
    reader.onerror = () => reject(new Error("No se pudo leer el archivo."));
    reader.readAsDataURL(file);
  });

// Galería para (re)asignar la foto del envase a un producto. Muestra las
// imágenes extraídas del PDF y permite subir una foto propia.
const ImageAssignModal = ({ open, images, currentUrl, onPick, onClose }: ImageAssignModalProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState("");
  const [urlValue, setUrlValue] = useState("");

  const onUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    try {
      const dataUrl = await fileToThumb(file);
      onPick(dataUrl);
      onClose();
    } catch {
      setError("No se pudo cargar la imagen. Prueba con un archivo más pequeño.");
    }
  };

  const applyUrl = () => {
    const v = urlValue.trim();
    if (!v) return;
    if (!/^https?:\/\//i.test(v)) {
      setError("Pega una URL de imagen válida (que empiece por http/https).");
      return;
    }
    setError("");
    onPick(v);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Elegir foto del producto"
      icon="ri-image-2-line"
      size="lg"
      footer={
        <>
          <button onClick={() => inputRef.current?.click()} className={ghostButtonClass}>
            <i className="ri-upload-2-line" /> Subir una foto
          </button>
          {currentUrl && (
            <button
              onClick={() => {
                onPick("");
                onClose();
              }}
              className={ghostButtonClass}
            >
              <i className="ri-delete-bin-line" /> Quitar foto
            </button>
          )}
        </>
      }
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={onUpload}
        className="hidden"
        aria-label="Subir foto del producto"
      />

      {error && (
        <p className="text-[11px] text-primary-200 mb-3 flex items-center gap-1.5">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}

      <div className="mb-4">
        <label className="text-[10px] text-foreground-500 mb-1 block">Pegar URL de la imagen</label>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
            placeholder="https://..."
            className="flex-1 bg-background-100/70 border border-background-300/50 rounded-md px-3 py-2 text-xs text-foreground-950 outline-none focus:border-accent-500/50"
            aria-label="URL de la imagen"
          />
          <button onClick={applyUrl} className={ghostButtonClass}>
            <i className="ri-link" /> Usar URL
          </button>
        </div>
      </div>

      {images.length === 0 ? (
        <div className="rounded-lg bg-background-200/40 border border-background-300/40 px-4 py-8 text-center">
          <span className="mx-auto w-12 h-12 rounded-lg bg-background-200/60 text-accent-200 flex items-center justify-center mb-3">
            <i className="ri-image-line text-2xl" />
          </span>
          <p className="text-sm text-foreground-700">
            Este PDF no tiene fotos extraíbles. Puedes subir una manualmente.
          </p>
        </div>
      ) : (
        <>
          <p className="text-[11px] text-foreground-500 mb-2">
            Fotos extraídas del PDF ({images.length}). Haz clic para asignarla a este producto.
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-[360px] overflow-y-auto">
            {images.map((img) => {
              const active = img.dataUrl === currentUrl;
              return (
                <button
                  key={img.id}
                  onClick={() => {
                    onPick(img.dataUrl);
                    onClose();
                  }}
                  title={`Página ${img.page}`}
                  className={`relative aspect-square rounded-md overflow-hidden border-2 cursor-pointer bg-background-200/40 ${
                    active ? "border-accent-500" : "border-transparent hover:border-accent-500/50"
                  }`}
                >
                  <img
                    src={img.dataUrl}
                    alt={`Foto página ${img.page}`}
                    className="w-full h-full object-contain"
                  />
                  <span className="absolute bottom-1 left-1 text-[9px] px-1.5 py-0.5 rounded bg-background-950/70 text-foreground-100">
                    p.{img.page}
                  </span>
                  {active && (
                    <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-accent-500 text-background-950 flex items-center justify-center">
                      <i className="ri-check-line text-xs" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </Modal>
  );
};

export default ImageAssignModal;